/**
 * AccountCommsSection — Communications card + dialogs + ViewAll for AccountOverview.
 */
import React, { useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Collapsible, CollapsibleContent } from '../../ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Skeleton } from '../../ui/skeleton';
import { MessageSquare, Mail, Phone } from 'lucide-react';
import { SectionHeader } from '../../common/OverviewHelpers';
import { CommunicationDialog, CommunicationDetailDialog } from '../ActivityDialogs';

const SectionSkeleton = ({ rows = 3 }) => (
  <div className="space-y-2">
    {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
  </div>
);

const getMethodIcon = (method) => {
  if (method === 'email_direct' || method === 'email_newsletter') return <Mail className="w-3 h-3" />;
  if (method === 'telephone') return <Phone className="w-3 h-3" />;
  return <MessageSquare className="w-3 h-3" />;
};

const AccountCommsSection = ({
  account, canEditAccount,
  comms = [], allComms = [], commsLoading,
  invalidateComms,
  users = [], usersMap = {},
  allAssociations = [], allTasks = [], contactEntries = [], horses = [],
}) => {
  const [open, setOpen] = useState(false);
  const [searchLocal, setSearchLocal] = useState('');
  const [commDialogOpen, setCommDialogOpen] = useState(false);
  const [commDetailOpen, setCommDetailOpen] = useState(false);
  const [selectedComm, setSelectedComm] = useState(null);
  const [allCommsDialogOpen, setAllCommsDialogOpen] = useState(false);

  const matchComm = (comm, q) => {
    if (!q) return true;
    const s = q.toLowerCase();
    const commDate = comm.start_date_time ? new Date(comm.start_date_time).toLocaleDateString() : '';
    const member = usersMap[comm.assigned_to_user_id] || '';
    return comm.activity_name?.toLowerCase().includes(s) ||
           comm.description?.toLowerCase().includes(s) ||
           comm.communication_method?.toLowerCase().includes(s) ||
           commDate.toLowerCase().includes(s) ||
           member.toLowerCase().includes(s);
  };

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <Collapsible open={open} onOpenChange={setOpen}>
            <SectionHeader
              title="Communications"
              icon={MessageSquare}
              isOpen={open}
              onToggle={() => setOpen(o => !o)}
              action={canEditAccount ? () => setCommDialogOpen(true) : undefined}
            />
            <CollapsibleContent className="mt-4">
              <div className="mb-3">
                <Input
                  placeholder="Search communications..."
                  value={searchLocal}
                  onChange={e => setSearchLocal(e.target.value)}
                  className="w-full"
                />
              </div>
              {commsLoading && <SectionSkeleton rows={3} />}
              {!commsLoading && (
                comms.filter(c => matchComm(c, searchLocal)).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No communications found.</p>
                ) : (
                  <div className="space-y-2">
                    {comms.filter(c => matchComm(c, searchLocal)).map(comm => (
                      <div
                        key={comm.id}
                        className="p-3 border rounded text-sm hover:bg-muted/50 cursor-pointer"
                        onClick={() => { setSelectedComm(comm); setCommDetailOpen(true); }}
                        data-testid={`comm-${comm.id}`}
                      >
                        <div className="flex justify-between items-center mb-1">
                          <Badge variant="secondary" className="text-xs flex items-center gap-1">
                            {getMethodIcon(comm.communication_method)}
                            {comm.communication_method?.replace('_', ' ')}
                          </Badge>
                          <span className="text-muted-foreground text-xs">
                            {comm.start_date_time ? new Date(comm.start_date_time).toLocaleDateString() : new Date(comm.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="font-medium">{comm.activity_name}</p>
                        {comm.description && <p className="text-muted-foreground mt-1 line-clamp-2">{comm.description}</p>}
                      </div>
                    ))}
                  </div>
                )
              )}
              {!commsLoading && (
                <div className="pt-3 border-t mt-3">
                  <Button variant="outline" size="sm" className="w-full" onClick={() => setAllCommsDialogOpen(true)}>
                    View All Communications ({allComms.length})
                  </Button>
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      <CommunicationDialog
        open={commDialogOpen}
        onOpenChange={setCommDialogOpen}
        accountId={account?.id}
        associations={allAssociations}
        users={users}
        contactEntries={contactEntries}
        tasks={allTasks}
        horses={horses}
        onSuccess={invalidateComms}
      />

      <CommunicationDetailDialog
        open={commDetailOpen}
        onOpenChange={setCommDetailOpen}
        communication={selectedComm}
        associations={allAssociations}
        users={users}
        usersMap={usersMap}
        contactEntries={contactEntries}
        tasks={allTasks}
        horses={horses}
        canEditAccount={canEditAccount}
        onSuccess={() => { invalidateComms?.(); setSelectedComm(null); }}
        onCommunicationUpdate={updated => { setSelectedComm(updated); invalidateComms?.(); }}
      />

      {/* View All Communications Dialog */}
      <Dialog open={allCommsDialogOpen} onOpenChange={setAllCommsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>All Communications</DialogTitle></DialogHeader>
          <div>
            <Input
              placeholder="Search by summary, notes, method, date, or team member..."
              value={searchLocal}
              onChange={e => setSearchLocal(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="flex-1 overflow-y-auto space-y-2">
            {allComms.filter(c => matchComm(c, searchLocal)).length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No communications found.</p>
            ) : allComms.filter(c => matchComm(c, searchLocal)).map(comm => (
              <div
                key={comm.id}
                className="flex items-start gap-3 p-3 border rounded hover:bg-muted/50 cursor-pointer"
                onClick={() => { setSelectedComm(comm); setAllCommsDialogOpen(false); setCommDetailOpen(true); }}
              >
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <Badge variant="secondary" className="text-xs flex items-center gap-1">
                      {getMethodIcon(comm.communication_method)}
                      {comm.communication_method?.replace('_', ' ')}
                    </Badge>
                    <span className="text-muted-foreground text-xs">
                      {comm.start_date_time ? new Date(comm.start_date_time).toLocaleDateString() : new Date(comm.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="font-medium text-sm">{comm.activity_name}</p>
                  {comm.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{comm.description}</p>}
                  {comm.assigned_to_user_id && (
                    <p className="text-xs text-muted-foreground mt-1">Team Member: {usersMap[comm.assigned_to_user_id] || 'Unknown'}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAllCommsDialogOpen(false); setSearchLocal(''); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AccountCommsSection;
